import React from 'react';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment-timezone';

export function formatDurationAgo(timestamp) {
  if (!timestamp) return 'Never Connected';
  const m = moment(timestamp);
  if (!m.isValid()) return 'Never Connected';
  return m.fromNow();
}

export function convertToTimeZone(requestDate) {
  let zone = sessionStorage.getItem('timeZone');
  const date = moment(new Date(requestDate));
  if (['null', null, 'undefined', undefined, ''].includes(zone)) {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return date.tz(zone).format('MM/DD/YYYY hh:mm:ss A');
  }
  return date
    .tz(zone.substring(zone.lastIndexOf(' ') + 1))
    .format('MM/DD/YYYY hh:mm:ss A');
}

function StoreCellRenderer(params) {
  const box = params.data || {};
  const account = box.account || {};
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
      <Avatar src={account.picture_url || undefined} sx={{ width: 28, height: 28 }}>
        {(account.name && account.name[0]) || '?'}
      </Avatar>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
        {account.name || '—'}
      </Typography>
    </Box>
  );
}

function StatusCellRenderer(params) {
  const isOnline = params.data && params.data.status === 1;
  return (
    <Chip
      size="small"
      label={isOnline ? 'Online' : 'Offline'}
      sx={{
        fontWeight: 600,
        backgroundColor: isOnline ? '#d1fae5' : '#f3f4f6',
        color: isOnline ? '#059669' : '#6b7280',
        border: `1px solid ${isOnline ? '#10b981' : '#9ca3af'}`,
      }}
    />
  );
}

function OnlineSinceCellRenderer(params) {
  const box = params.data || {};
  const isOnline = box.status === 1;
  const ts = isOnline ? box.onlineSince : box.offlineSince;
  if (!ts) {
    return (
      <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#9ca3af' }}>
        Never Connected
      </Typography>
    );
  }
  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: isOnline ? '#059669' : '#ef4444',
          fontSize: '0.8rem',
          lineHeight: 1.2,
        }}
      >
        {formatDurationAgo(ts)}
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
        {convertToTimeZone(ts)}
      </Typography>
    </Box>
  );
}

function healthChipSx(status) {
  if (status === 'healthy') {
    return {
      backgroundColor: '#d1fae5',
      color: '#059669',
      border: '1px solid #10b981',
    };
  }
  if (status === 'unhealthy') {
    return {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      border: '1px solid #ef4444',
    };
  }
  return {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    border: '1px solid #9ca3af',
  };
}

function healthLabel(status) {
  if (status === 'healthy') return 'Healthy';
  if (status === 'unhealthy') return 'Unhealthy';
  return 'Unknown';
}

function healthTimestampColor(status) {
  if (status === 'healthy') return '#059669';
  if (status === 'unhealthy') return '#dc2626';
  return '#9ca3af';
}

const SERVICE_HEALTH_CELL_STYLE = {
  display: 'flex',
  alignItems: 'center',
  lineHeight: 'normal',
  overflow: 'hidden',
};

function ServiceHealthCellRenderer({ health }) {
  const status = (health && health.status) || 'unknown';
  const lastSeen = health && health.lastSeen;
  const reason = health && health.reason;
  const tooltip = [
    healthLabel(status),
    lastSeen ? `Last updated ${formatDurationAgo(lastSeen)}` : 'No heartbeat received',
    lastSeen ? convertToTimeZone(lastSeen) : null,
    reason === 'stale' ? 'Heartbeat is stale' : null,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '2px',
          minWidth: 0,
          maxWidth: '100%',
          lineHeight: 1.2,
          overflow: 'hidden',
        }}
      >
        <Chip
          size="small"
          label={healthLabel(status)}
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 22,
            maxHeight: 22,
            width: 'fit-content',
            maxWidth: '100%',
            flexShrink: 0,
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              px: 1,
            },
            ...healthChipSx(status),
          }}
        />
        <Typography
          variant="caption"
          noWrap
          sx={{
            fontSize: '0.7rem',
            fontWeight: lastSeen ? 600 : 400,
            fontStyle: lastSeen ? 'normal' : 'italic',
            color: lastSeen ? healthTimestampColor(status) : '#9ca3af',
            lineHeight: 1.2,
          }}
        >
          {lastSeen ? formatDurationAgo(lastSeen) : 'Never updated'}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function EngineHealthCellRenderer(params) {
  return <ServiceHealthCellRenderer health={params.data && params.data.kioskEngineHealth} />;
}

function PosBridgeHealthCellRenderer(params) {
  return <ServiceHealthCellRenderer health={params.data && params.data.posBridgeHealth} />;
}

export function getLocationColumnDefs() {
  return [
    {
      headerName: 'Store',
      colId: 'store',
      flex: 1.4,
      minWidth: 160,
      valueGetter: (p) =>
        (p.data && p.data.account && p.data.account.name) || '',
      cellRenderer: StoreCellRenderer,
    },
    {
      headerName: 'Location',
      field: 'name',
      flex: 1.2,
      minWidth: 140,
    },
    {
      headerName: 'Status',
      colId: 'status',
      width: 110,
      valueGetter: (p) =>
        p.data && p.data.status === 1 ? 'Online' : 'Offline',
      cellRenderer: StatusCellRenderer,
    },
    {
      headerName: 'Online / Offline',
      colId: 'onlineSince',
      flex: 1.3,
      minWidth: 150,
      valueGetter: (p) => {
        const box = p.data || {};
        const isOnline = box.status === 1;
        const ts = isOnline ? box.onlineSince : box.offlineSince;
        return ts ? formatDurationAgo(ts) : 'Never Connected';
      },
      cellRenderer: OnlineSinceCellRenderer,
    },
    {
      headerName: 'Engine',
      colId: 'kioskEngineHealth',
      width: 150,
      minWidth: 140,
      cellStyle: SERVICE_HEALTH_CELL_STYLE,
      cellClass: 'ilocal-service-health-cell',
      valueGetter: (p) => {
        const health = (p.data && p.data.kioskEngineHealth) || {};
        const status = healthLabel(health.status);
        return health.lastSeen ? `${status} ${formatDurationAgo(health.lastSeen)}` : status;
      },
      cellRenderer: EngineHealthCellRenderer,
    },
    {
      headerName: 'POS Bridge',
      colId: 'posBridgeHealth',
      width: 150,
      minWidth: 140,
      cellStyle: SERVICE_HEALTH_CELL_STYLE,
      cellClass: 'ilocal-service-health-cell',
      valueGetter: (p) => {
        const health = (p.data && p.data.posBridgeHealth) || {};
        const status = healthLabel(health.status);
        return health.lastSeen ? `${status} ${formatDurationAgo(health.lastSeen)}` : status;
      },
      cellRenderer: PosBridgeHealthCellRenderer,
    },
    {
      headerName: 'Utilization %',
      field: 'box_Utilization',
      width: 120,
      valueFormatter: (p) =>
        p.value == null || p.value === '' ? '—' : `${p.value}%`,
    },
    {
      headerName: 'Available Bins',
      field: 'available_Bins',
      width: 130,
      valueFormatter: (p) => (p.value == null || p.value === '' ? '—' : p.value),
    },
  ];
}

export function getLiveAgentColumnDefs() {
  return [
    {
      headerName: 'Account',
      field: 'account',
      flex: 1.2,
      minWidth: 140,
    },
    {
      headerName: 'Username',
      field: 'name',
      flex: 1,
      minWidth: 120,
    },
    {
      headerName: 'Consult',
      colId: 'is_user_consult',
      width: 110,
      valueGetter: (p) => (p.data && p.data.is_user_consult ? 'Yes' : 'No'),
      valueFormatter: (p) =>
        p.data && p.data.is_user_consult ? '🟢' : '🔴',
    },
    {
      headerName: 'Last Login',
      field: 'last_login_on',
      flex: 1.2,
      minWidth: 160,
      valueFormatter: (p) => (p.value ? convertToTimeZone(p.value) : '—'),
    },
  ];
}

export const TURNOVER_REPORT_COLS = [
  { headerName: 'First Name', field: 'FirstName', flex: 1, minWidth: 110 },
  { headerName: 'Last Name', field: 'LastName', flex: 1, minWidth: 110 },
  { headerName: 'Store', field: 'Store', flex: 1, minWidth: 120 },
  { headerName: 'Box', field: 'Box', flex: 1, minWidth: 100 },
  { headerName: 'Stock Code', field: 'StockCode', flex: 1, minWidth: 110 },
  { headerName: 'PU Code', field: 'PuCode', flex: 1, minWidth: 100 },
  { headerName: 'Stocked', field: 'StockedDate', flex: 1.1, minWidth: 140 },
  { headerName: 'Picked Up', field: 'PickupedDate', flex: 1.1, minWidth: 140 },
  { headerName: 'Duration', field: 'Duration', width: 110 },
];

export const TX_TIME_REPORT_COLS = [
  { headerName: 'User', field: 'SessionUserName', flex: 1, minWidth: 120 },
  { headerName: 'Active', field: 'SessionActive', width: 90 },
  { headerName: 'Account', field: 'AccountName', flex: 1, minWidth: 120 },
  { headerName: 'Box', field: 'BoxName', flex: 1, minWidth: 100 },
  { headerName: 'Start', field: 'SessionStartOn', flex: 1.1, minWidth: 140 },
  { headerName: 'End', field: 'SessionEndOn', flex: 1.1, minWidth: 140 },
  { headerName: 'Duration', field: 'SessionDuration', width: 110 },
];

export const ADHERENCE_REPORT_COLS = [
  { headerName: 'First Name', field: 'firstname', flex: 1, minWidth: 100 },
  { headerName: 'Last Name', field: 'lastname', flex: 1, minWidth: 100 },
  { headerName: 'Store', field: 'store', flex: 1, minWidth: 110 },
  { headerName: 'Box', field: 'box', flex: 1, minWidth: 90 },
  { headerName: 'Bin', field: 'binid', width: 80 },
  { headerName: 'Rx #', field: 'rxno', flex: 1, minWidth: 100 },
  { headerName: 'Stock Code', field: 'stock_code', flex: 1, minWidth: 110 },
  { headerName: 'PU Code', field: 'pucode', flex: 1, minWidth: 100 },
  { headerName: 'NDC', field: 'ndccode', flex: 1, minWidth: 110 },
  { headerName: 'Qty', field: 'qty', width: 70 },
  { headerName: 'Status', field: 'status', flex: 1, minWidth: 100 },
  { headerName: 'User', field: 'username', flex: 1, minWidth: 110 },
];

function ImageCellRenderer(params) {
  const src = params.value;
  if (!src) {
    return (
      <Typography variant="caption" color="textSecondary">
        —
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <img
        src={src}
        alt=""
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          objectFit: 'cover',
        }}
      />
    </Box>
  );
}

export const PICKUP_REPORT_COLS = [
  { headerName: 'First Name', field: 'firstname', flex: 1, minWidth: 100 },
  { headerName: 'Last Name', field: 'lastname', flex: 1, minWidth: 100 },
  { headerName: 'Store', field: 'store', flex: 1, minWidth: 110 },
  { headerName: 'Box', field: 'box', flex: 1, minWidth: 90 },
  { headerName: 'Bin', field: 'binid', width: 80 },
  { headerName: 'Rx #', field: 'rxno', flex: 1, minWidth: 100 },
  { headerName: 'Stock Code', field: 'stock_code', flex: 1, minWidth: 110 },
  { headerName: 'PU Code', field: 'pucode', flex: 1, minWidth: 100 },
  { headerName: 'NDC', field: 'ndccode', flex: 1, minWidth: 110 },
  { headerName: 'Qty', field: 'qty', width: 70 },
  { headerName: 'Fill Date', field: 'filldate', flex: 1, minWidth: 120 },
  {
    headerName: 'Pickup Date',
    field: 'pickupdate',
    flex: 1.2,
    minWidth: 150,
    valueFormatter: (p) => (p.value ? convertToTimeZone(p.value) : ''),
  },
  {
    headerName: 'Signature',
    field: 'signatureurl',
    width: 90,
    cellRenderer: ImageCellRenderer,
    sortable: false,
    filter: false,
  },
  {
    headerName: 'Picture',
    field: 'pictureurl',
    width: 90,
    cellRenderer: ImageCellRenderer,
    sortable: false,
    filter: false,
  },
  { headerName: 'User', field: 'username', flex: 1, minWidth: 110 },
];

export const CONSULTATION_REPORT_COLS = [
  { headerName: 'Store', field: 'store_name', flex: 1, minWidth: 120 },
  { headerName: 'Kiosk', field: 'kiosk_name', flex: 1, minWidth: 110 },
  { headerName: 'Date', field: 'date', flex: 1, minWidth: 110 },
  { headerName: 'Start', field: 'start_time', flex: 1, minWidth: 100 },
  { headerName: 'End', field: 'end_time', flex: 1, minWidth: 100 },
  { headerName: 'Duration', field: 'call_duration', width: 100 },
  { headerName: 'Status', field: 'Status', flex: 1, minWidth: 100 },
  { headerName: 'Pickup Code', field: 'pickup_code', flex: 1, minWidth: 110 },
  { headerName: 'User', field: 'user_name', flex: 1, minWidth: 110 },
  { headerName: 'Consultation', field: 'consultation_name', flex: 1, minWidth: 120 },
];

export const ORDERS_REPORT_COLS = [
  { headerName: 'Store', field: 'store', flex: 1, minWidth: 120 },
  { headerName: 'Box', field: 'box', flex: 1, minWidth: 100 },
  { headerName: 'Bin', field: 'binId', width: 80 },
  { headerName: 'Stock Code', field: 'stock_code', flex: 1, minWidth: 110 },
  { headerName: 'PU Code', field: 'pucode', flex: 1, minWidth: 100 },
  { headerName: 'Stocked', field: 'stockeddate', flex: 1.1, minWidth: 140 },
  { headerName: 'Picked Up', field: 'pickupdate', flex: 1.1, minWidth: 140 },
  { headerName: 'Status', field: 'status', flex: 1, minWidth: 110 },
];

export const STOCK_AGE_REPORT_COLS = [
  { headerName: 'Store', field: 'store', flex: 1, minWidth: 120 },
  { headerName: 'Box', field: 'box', flex: 1, minWidth: 100 },
  { headerName: 'Bin', field: 'binId', width: 80 },
  { headerName: 'Stock Code', field: 'stockCode', flex: 1, minWidth: 110 },
  { headerName: 'Pickup Code', field: 'pickupCode', flex: 1, minWidth: 110 },
  { headerName: 'Stocked', field: 'stockedDate', flex: 1.1, minWidth: 140 },
];

/** Donut-style stand-in for Apex gauges (AG Charts Community has no radial gauge). */
export function buildGaugeDonutOptions({ value, max, label, unit, color }) {
  const v = Math.max(0, Number(value) || 0);
  const m = Math.max(max || 1, v, 1);
  const remainder = Math.max(0, m - v);
  return {
    data: [
      { segment: label, amount: v },
      { segment: 'Remaining', amount: remainder },
    ],
    height: 260,
    legend: { enabled: false },
    series: [
      {
        type: 'pie',
        angleKey: 'amount',
        legendItemKey: 'segment',
        innerRadiusRatio: 0.65,
        fills: [color || '#6cca98', '#E5E7EB'],
        strokes: ['#fff', '#fff'],
        calloutLabel: { enabled: false },
        sectorLabel: { enabled: false },
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.segment,
            content: `${datum.amount}${unit ? ` ${unit}` : ''}`,
          }),
        },
      },
    ],
  };
}



// WEBPACK FOOTER //
// ./src/components/dashboard2/dashboardColumns.js